import './grid.css';

/**
 * CDH Download Grid — Frontend
 *
 * Handles search, filtering, AJAX loading, pagination and tile rendering
 * for every .cdh-download-grid-wrapper on the page.
 */
(function () {
	'use strict';

	const API_URL = (window.cdhGrid && window.cdhGrid.apiUrl) || '/wp-json/cdh-grid/v1/downloads';
	const WP_API_URL = (window.cdhGrid && window.cdhGrid.wpApiUrl) || '/wp-json/wp/v2/';
	const NONCE = (window.cdhGrid && window.cdhGrid.nonce) || '';
	const HIDDEN_TAG = (window.cdhGrid && window.cdhGrid.hiddenTag) || '';
	const DEBOUNCE_MS = 300;
	const DEFAULT_PER_PAGE = 20;

	document.addEventListener('DOMContentLoaded', () => {
		document.querySelectorAll('.cdh-download-grid-wrapper').forEach(initGrid);
	});

	function initGrid(wrapper) {
		const dataset = wrapper.dataset;
		const baseCatSlugs = dataset.categories ? dataset.categories.split(',').filter(Boolean) : [];
		const baseTagSlugs = dataset.tags ? dataset.tags.split(',').filter(Boolean) : [];
		const headingTag = 'h' + (Math.min(6, Math.max(2, parseInt(dataset.headingLevel, 10) || 3)));
		const perPage = Math.max(1, Math.min(100, parseInt(dataset.perPage, 10) || DEFAULT_PER_PAGE));

		// DOM
		const searchInput = wrapper.querySelector('.cdh-grid-search');
		const catSelect = wrapper.querySelector('.cdh-grid-cat-select');
		const tagSelect = wrapper.querySelector('.cdh-grid-tag-select');
		const sortSelect = wrapper.querySelector('.cdh-grid-sort-select');
		const countEl = wrapper.querySelector('.cdh-grid-count');
		const resetBtn = wrapper.querySelector('.cdh-grid-reset');
		const resultsEl = wrapper.querySelector('.cdh-grid-results');
		const spinner = wrapper.querySelector('.cdh-grid-spinner');

		if (resultsEl && dataset.columns) {
			resultsEl.classList.add('cdh-grid-results--cols-' + dataset.columns);
		}
		const paginationEl = wrapper.querySelector('.cdh-grid-pagination');

		// State
		let activeCat = '';
		let activeTag = '';
		let activeSearch = '';
		let activeOrderby = 'title';
		let activeOrder = 'ASC';
		let currentPage = 1;
		let totalItems = 0;
		let totalPages = 1;
		let debounceTimer = null;

		// ---- Initial load --------------------------------------------------------
		fetchAndRender();
		if (catSelect) populateSelectHierarchy('dlm_download_category', catSelect, baseCatSlugs);
		if (tagSelect) populateSelectHierarchy('dlm_download_tag', tagSelect);

		// ---- Event listeners -----------------------------------------------------
		if (searchInput) {
			searchInput.addEventListener('input', () => {
				clearTimeout(debounceTimer);
				debounceTimer = setTimeout(() => {
					activeSearch = searchInput.value.trim();
					currentPage = 1;
					updateResetVisibility();
					fetchAndRender();
				}, DEBOUNCE_MS);
			});
		}

		if (catSelect) {
			catSelect.addEventListener('change', () => {
				activeCat = catSelect.value;
				currentPage = 1;
				updateResetVisibility();
				fetchAndRender();
			});
		}

		if (tagSelect) {
			tagSelect.addEventListener('change', () => {
				activeTag = tagSelect.value;
				currentPage = 1;
				updateResetVisibility();
				fetchAndRender();
			});
		}

		if (sortSelect) {
			sortSelect.addEventListener('change', () => {
				const [orderby, order] = sortSelect.value.split(':');
				activeOrderby = orderby;
				activeOrder = order;
				currentPage = 1;
				fetchAndRender();
			});
		}

		if (resetBtn) {
			resetBtn.addEventListener('click', () => {
				activeSearch = '';
				activeCat = '';
				activeTag = '';
				currentPage = 1;

				if (searchInput) searchInput.value = '';
				if (catSelect) catSelect.value = '';
				if (tagSelect) tagSelect.value = '';

				updateResetVisibility();
				fetchAndRender();
			});
		}

		// ---- Core fetch ----------------------------------------------------------
		function fetchAndRender() {
			setLoading(true);

			const catParam = activeCat ? activeCat : baseCatSlugs.join(',');
			const tagParam = activeTag ? activeTag : baseTagSlugs.join(',');

			const params = new URLSearchParams();
			if (activeSearch) params.set('search', activeSearch);
			if (catParam) params.set('category', catParam);
			if (tagParam) params.set('tag', tagParam);
			params.set('orderby', activeOrderby);
			params.set('order', activeOrder);
			params.set('per_page', String(perPage));
			params.set('page', String(currentPage));

			const url = API_URL + (params.toString() ? '?' + params.toString() : '');

			fetch(url, {
				headers: { 'X-WP-Nonce': NONCE, 'Accept': 'application/json' },
			})
				.then((res) => {
					if (!res.ok) throw new Error(res.statusText);
					totalItems = parseInt(res.headers.get('X-WP-Total') || '0', 10);
					totalPages = parseInt(res.headers.get('X-WP-TotalPages') || '1', 10);
					return res.json();
				})
				.then((downloads) => {
					renderResults(downloads);
					updateCount();
					renderPagination();
					setLoading(false);
					moveFocusToFirst();
				})
				.catch(() => {
					if (resultsEl) {
						resultsEl.innerHTML = '<p class="cdh-grid-error">Die Downloads konnten nicht geladen werden.</p>';
					}
					setLoading(false);
				});
		}

		// ---- Render helpers ------------------------------------------------------
		function renderResults(downloads) {
			if (!resultsEl) return;
			if (downloads.length === 0) {
				resultsEl.innerHTML = '<p class="cdh-grid-empty">Keine Downloads gefunden.</p>';
				return;
			}
			resultsEl.innerHTML = downloads.map(renderTile).join('');
		}

		function renderTile(download) {
			const hasThumbnail = download.thumbnail && download.thumbnail !== '';
			const hasFeatured = download.featured === true;
			const hasExcerpt = download.description && download.description.trim() !== '';
			const titleId = 'cdh-download-title-' + download.id;

			// soll zunächst ausgeblendet bleiben da die meisten Tiles kein Bild haben und das Layout dadurch springt. Sobald die Bilder geladen sind, werden die Tiles sichtbar gemacht (siehe grid.css)
			const thumbnailHTML = hasThumbnail
				? `<div class="cdh-grid-tile__thumbnail"><img src="${escAttr(download.thumbnail)}" alt="" loading="lazy" /></div>`
				: '';

			const featuredHTML = hasFeatured
				? `<span class="cdh-grid-tile__featured-badge">Hervorgehoben</span>`
				: '';

			const catLabels = (download.categories || [])
				.map((c) => escHtml(c.path || c.name || ''))
				.filter(Boolean)
				.join(', ');
			const metaHTML = catLabels ? `<span class="cdh-grid-tile__meta">${catLabels}</span>` : '';

			const excerptHTML = hasExcerpt ? `<p class="cdh-grid-tile__excerpt">${escHtml(download.description)}</p>` : '';
			const versionTitelHTML = download.versions && download.versions.length > 1 ? `<span class="cdh-grid-tile__versions-headline">Alle Versionen</span>` : '';

			const versionCount = download.versions ? download.versions.length : 0;
			const versionCountHTML = `<span class="cdh-grid-tile__version-count">${versionCount} ${versionCount === 1 ? 'Version' : 'Versionen'}</span>`;
			const downloadCount = download.download_count || 0;
			const downloadCountHTML = `<span class="cdh-grid-tile__download-count">${downloadCount} ${downloadCount === 1 ? 'Download' : 'Downloads'}</span>`;

			return `
				<article class="cdh-grid-tile${hasThumbnail ? '' : ' cdh-grid-tile--no-image'}${hasFeatured ? ' cdh-grid-tile--featured' : ''}" aria-labelledby="${escAttr(titleId)}">
					<div class="cdh-grid-tile__body">
						<div class="content">
							<${headingTag} class="cdh-grid-tile__title" id="${escAttr(titleId)}">${escHtml(download.title)}</${headingTag}>
							${excerptHTML}
							</div>
							<div class="cdh-grid-tile__versions">${versionTitelHTML}${renderVersionButtons(download)}</div>
							<footer class="cdh-grid-tile__footer">
								${metaHTML}
								${featuredHTML}
								${versionCountHTML}
						</footer>
					</div>
				</article>`;
		}

		function renderVersionButtons(download) {
			if (!download.versions || download.versions.length === 0) return '';
			const isSingle = download.versions.length === 1;
			const MAX_VISIBLE = 2;

			const renderBtn = (version, index) => {
				const label = isSingle
					? (version.label ? escHtml(version.label) + ' – Herunterladen' : 'Herunterladen')
					: (escHtml(version.label) || 'Standard');
				const type = escHtml(version.file_type);
				const size = escHtml(version.file_size);
				const url = escUrl(version.download_url);
				const isPrimary = index === 0;

				const ariaLabel = [
					escAttr(download.title) + ' Herunterladen',
					isSingle ? null : 'Version ' + escAttr(version.label),
					type,
					size,
				].filter(Boolean).join(', ');

				// <span class="cdh-grid-version-btn__icon" aria-hidden="true">↓</span>

				const forceDownload = type && type !== 'PDF' ? ' download' : '';

				return `<a href="${url}" class="cdh-grid-version-btn${isPrimary ? ' cdh-grid-version--primary' : ''}" aria-label="${ariaLabel}" target="_blank" rel="noopener noreferrer" data-redirect="true"${forceDownload}>
					<span class="cdh-grid-version-btn__label">${label}</span>
					${type ? `<span class="cdh-grid-version-btn__type">${type}</span>` : ''}
					${size ? `<span class="cdh-grid-version-btn__size">${size}</span>` : ''}
				</a>`;
			};

			if (isSingle) {
				return renderBtn(download.versions[0], 0);
			}

			const visibleVersions = download.versions.slice(0, MAX_VISIBLE);
			const hiddenVersions = download.versions.slice(MAX_VISIBLE);

			const visibleItems = visibleVersions.map((v, i) => `<li>${renderBtn(v, i)}</li>`).join('\n');

			if (hiddenVersions.length === 0) {
				return `<ul class="cdh-grid-versions-list" aria-label="Versionen">\n${visibleItems}\n</ul>`;
			}

			const hiddenCount = hiddenVersions.length;
			const hiddenItems = hiddenVersions.map((v, i) => `<li>${renderBtn(v, MAX_VISIBLE + i)}</li>`).join('\n');

			return `<ul class="cdh-grid-versions-list" aria-label="Versionen">
${visibleItems}
<li class="cdh-grid-versions-more">
<details>
	<summary class="cdh-grid-versions-more__toggle">${hiddenCount} weitere ${hiddenCount === 1 ? 'Version' : 'Versionen'} anzeigen</summary>
	<ul class="cdh-grid-versions-list">
${hiddenItems}
	</ul>
</details>
</li>
</ul>`;
		}

		// ---- Pagination ----------------------------------------------------------
		function renderPagination() {
			if (!paginationEl) return;

			if (totalPages <= 1) {
				paginationEl.hidden = true;
				paginationEl.innerHTML = '';
				return;
			}

			paginationEl.hidden = false;
			paginationEl.innerHTML = `
				<button class="cdh-grid-page-btn cdh-grid-page-btn--prev" ${currentPage <= 1 ? 'disabled' : ''} data-page="${currentPage - 1}" aria-label="Vorherige Seite">zurück</button>
				<span class="cdh-grid-page-info" aria-live="polite">Seite ${currentPage} von ${totalPages}</span>
				<button class="cdh-grid-page-btn cdh-grid-page-btn--next" ${currentPage >= totalPages ? 'disabled' : ''} data-page="${currentPage + 1}" aria-label="Nächste Seite">weiter</button>
			`;

			paginationEl.querySelectorAll('.cdh-grid-page-btn:not([disabled])').forEach((btn) => {
				btn.addEventListener('click', () => {
					currentPage = parseInt(btn.dataset.page, 10);
					fetchAndRender();
				});
			});
		}

		// ---- Taxonomy selects ----------------------------------------------------
		function populateSelectHierarchy(taxonomy, selectEl, selectedSlugs = []) {
			fetch(WP_API_URL + taxonomy + '?per_page=100&hide_empty=true&_fields=id,name,slug,parent&orderby=name&order=asc', {
				headers: { 'X-WP-Nonce': NONCE, 'Accept': 'application/json' },
			})
				.then((r) => r.ok ? r.json() : [])
				.catch(() => [])
				.then((terms) => {
					const filtered = (HIDDEN_TAG && taxonomy === 'dlm_download_tag')
						? terms.filter((t) => t.slug !== HIDDEN_TAG)
						: terms;
					const opts = taxonomy === 'dlm_download_category' && selectedSlugs.length > 1
						? buildSelectedCategoryOptions(filtered, selectedSlugs)
						: buildHierarchyOptions(filtered, 0, 0);
					while (selectEl.options.length > 1) selectEl.remove(1);
					opts.forEach((opt) => selectEl.appendChild(opt));

					const wrap = selectEl.closest('.cdh-grid-filter-wrap');
					if (wrap) wrap.hidden = opts.length === 0;
				});
		}

		function buildSelectedCategoryOptions(terms, selectedSlugs) {
			const selectedSet = new Set(selectedSlugs);
			const termById = new Map(terms.map((term) => [term.id, term]));
			const childrenByParent = new Map();

			terms.forEach((term) => {
				const parentId = term.parent || 0;
				if (!childrenByParent.has(parentId)) childrenByParent.set(parentId, []);
				childrenByParent.get(parentId).push(term);
			});

			const rootTerms = terms.filter((term) => selectedSet.has(term.slug));
			if (rootTerms.length === 0) return buildHierarchyOptions(terms, 0, 0);

			const allowedIds = new Set();
			const collectChildren = (parentId) => {
				(childrenByParent.get(parentId) || []).forEach((child) => {
					if (allowedIds.has(child.id)) return;
					allowedIds.add(child.id);
					collectChildren(child.id);
				});
			};

			rootTerms.forEach((root) => {
				allowedIds.add(root.id);
				collectChildren(root.id);
			});

			const filteredTerms = terms.filter((term) => allowedIds.has(term.id));
			const roots = filteredTerms.filter((term) => !termById.has(term.parent) || !allowedIds.has(term.parent));

			return roots.flatMap((root) => {
				const opt = document.createElement('option');
				opt.value = root.slug;
				opt.textContent = root.name;
				return [opt, ...buildHierarchyOptions(filteredTerms, root.id, 1)];
			});
		}

		function buildHierarchyOptions(terms, parentId, level) {
			return terms
				.filter((t) => (t.parent || 0) === parentId)
				.flatMap((t) => {
					const opt = document.createElement('option');
					opt.value = t.slug;
					opt.textContent = (level > 0 ? '\u2014'.repeat(level) + ' ' : '') + t.name;
					return [opt, ...buildHierarchyOptions(terms, t.id, level + 1)];
				});
		}

		// ---- UI state helpers ----------------------------------------------------
		function setLoading(isLoading) {
			wrapper.setAttribute('aria-busy', isLoading ? 'true' : 'false');
			if (spinner) spinner.hidden = !isLoading;
		}

		function updateCount() {
			if (!countEl) return;
			const isFiltered = activeSearch !== '' || activeCat !== '' || activeTag !== '';

			if (isFiltered) {
				countEl.textContent = totalItems === 1
					? '1 Download gefunden'
					: totalItems + ' Downloads gefunden';
			} else {
				if (totalItems === 0) {
					countEl.textContent = 'Keine Downloads';
					return;
				}
				const start = (currentPage - 1) * perPage + 1;
				const end = Math.min(currentPage * perPage, totalItems);
				countEl.textContent = start + '–' + end + ' von ' + totalItems + ' Downloads';
			}
		}

		function updateResetVisibility() {
			if (!resetBtn) return;
			resetBtn.hidden = activeSearch === '' && activeCat === '' && activeTag === '';
		}

		function moveFocusToFirst() {
			if (!resultsEl) return;
			const first = resultsEl.querySelector('.cdh-grid-tile');
			if (first) first.setAttribute('tabindex', '-1');
		}

		// ---- Security helpers ----------------------------------------------------
		function escHtml(str) {
			const div = document.createElement('div');
			div.textContent = String(str);
			return div.innerHTML;
		}

		function escAttr(str) {
			return String(str)
				.replace(/&/g, '&amp;')
				.replace(/"/g, '&quot;')
				.replace(/'/g, '&#039;')
				.replace(/</g, '&lt;')
				.replace(/>/g, '&gt;');
		}

		function escUrl(str) {
			const url = String(str).trim();
			if (/^javascript:/i.test(url)) return '#';
			return escAttr(url);
		}
	}
})();
